// AuditLog.cshtml (QOR staff): who did what — nothing loads until a filter is applied; CSV export of the filter.
import { dateTimeUtc } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, totalPages } from '../api';
import { allAuditEntries, auditCsv, auditRange } from '../audit';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { Pagination } from '../components/ui';
import { download } from '../points';
import { actionBadgeClass } from '../scope';

const PAGE_SIZE = 25;

export function AuditLogPage() {
  usePageTitle('Audit Log');
  const client = useQueryClient();
  const q = useQueryParams();
  const filtered = q.flag('filtered');
  const f = {
    actor: q.get('actor'),
    target: q.get('target'),
    action: q.get('action'),
    from: q.get('from'),
    to: q.get('to'),
  };
  const page = q.page();
  const submit = useFilterForm('/audit-log');
  const results = useQuery({
    queryKey: ['audit', filtered, f, page],
    queryFn: async () =>
      filtered
        ? adminApi.auditLog({ ...f, ...auditRange(f), page, pageSize: PAGE_SIZE })
        : // only the action list, for the filter
          { ...(await adminApi.auditLog({ page: 1, pageSize: 1 })), items: [] },
  });
  const [exporting, setExporting] = useState(false);

  async function exportCsv() {
    setExporting(true);
    try {
      download('qor-audit-log.csv', auditCsv(await allAuditEntries(f)));
    } finally {
      setExporting(false);
    }
  }

  const data = results.data;
  return (
    <>
      <div className="qc-page-heading-row">
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            Audit Log <span className="qc-badge qc-badge-accent">Live Feed</span>
          </h1>
          <p className="qc-scope-label" style={{ marginTop: 6 }}>
            Showing system events, partner API calls &amp; kiosk transactions
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {filtered && (
            <button type="button" className="qc-button-ghost" disabled={exporting} onClick={() => void exportCsv()}>
              Export CSV
            </button>
          )}
          <button
            type="button"
            className="qc-button-ghost"
            aria-label="Refresh"
            onClick={() => void client.invalidateQueries({ queryKey: ['audit'] })}
          >
            &#8635;
          </button>
        </div>
      </div>

      <form className="qc-cardholder-filters" onSubmit={submit} key={toQuery({ ...f, filtered })}>
        <input type="hidden" name="filtered" value="true" />
        <div className="qc-search-wrap">
          <input
            type="search"
            name="actor"
            defaultValue={f.actor ?? ''}
            placeholder="Search by actor (who did it)..."
            autoComplete="off"
          />
        </div>
        <div className="qc-search-wrap">
          <input
            type="search"
            name="target"
            defaultValue={f.target ?? ''}
            placeholder="Search by target (who/what it happened to)..."
            autoComplete="off"
          />
        </div>
        <select name="action" aria-label="Filter by action" defaultValue={f.action ?? ''}>
          <option value="">All actions</option>
          {(data?.availableActions ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <input type="date" name="from" defaultValue={f.from ?? ''} aria-label="From date" />
        <input type="date" name="to" defaultValue={f.to ?? ''} aria-label="To date" />
        <button type="submit" className="qor-button qor-button-primary">
          Filter
        </button>
        {filtered && (
          <Link className="qc-button-ghost" to="/audit-log">
            Clear
          </Link>
        )}
      </form>

      <div className="qc-panel">
        {!filtered ? (
          <p className="qc-empty">
            Choose a filter above and click Filter to see activity -- nothing loads until you ask for it.
          </p>
        ) : !data ? null : data.items.length === 0 ? (
          <p className="qc-empty">No matching activity.</p>
        ) : (
          <>
            <div className="qc-table-scroll">
              <table className="qc-plain-table qc-audit-table">
                <thead>
                  <tr>
                    <th>When (UTC)</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((e) => (
                    <tr key={e.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{dateTimeUtc(e.occurredAt)}</td>
                      <td>
                        <div className="qc-user-row">
                          <span className="qc-row-dot" aria-hidden="true"></span>
                          <div>
                            <strong>{e.actorDisplayName}</strong>
                            <br />
                            <code className="qc-code-chip">{e.actorEmail}</code>
                            {e.actorIpAddress && e.actorIpAddress.trim() !== '' && (
                              <>
                                <br />
                                <code className="qc-code-chip qc-code-chip-muted">{e.actorIpAddress}</code>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`qc-badge ${actionBadgeClass(e.action)}`}>{e.action}</span>
                      </td>
                      <td>
                        <code className="qc-code-chip">
                          {e.targetType}: {e.targetLabel}
                        </code>
                      </td>
                      <td className="qc-audit-details">{e.details ?? '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={totalPages(data)}
              totalCount={data.totalCount}
              href={(p) =>
                `/audit-log${toQuery({ filtered: true, actor: f.actor, target: f.target, action: f.action, from: f.from, to: f.to, page: p })}`
              }
            />
          </>
        )}
      </div>
    </>
  );
}
